import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Grid, Paper, Button,
  Snackbar,
  Chip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { invokeApig } from '../libs/awsLib';
import NeokodeButton from '../components/NeokodeButton';
import DataTable from '../components/DataTable';
import QuickSearchToolbar from '../components/QuickSearchToolbar';
import moment from 'moment';
import FilterPanel from '../containers/support/FilterPanel';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
  },
  gridMaxMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.breakpoints.values.lg,
    },
  },
  button: {
    margin: '15px',
  },
  green: {
    backgroundColor: '#00b31f',
    color: '#ffffff',
  },
  red: {
    backgroundColor: '#f44336',
    color: '#ffffff',
  },
  yellow: {
    backgroundColor: '#ff9800',
    color: '#ffffff',
  },
  blue: {
    backgroundColor: '#2196f3',
    color: '#ffffff',
  },
}));

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function TicketsPage(props) {
  const history = useHistory();
  const classes = useStyles();
  const [isGlobalError, setIsGlobalError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [tickets, setTickets] = useState([]);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [messageToast, setMessageToast] = useState('');

  useEffect(() => {
    getTickets();
  }, []);

  const getTickets = async (filter) => {
    try {
      setIsLoading(true);
      setIsGlobalError(false);
      setTickets([]);
      setRows([]);
      setSearchText('');
      let lastKey = undefined;
      if (!filter) {
        filter = {};
      }
      let newTickets = [];
      do {
        const res = await invokeApig({
          path: "/support/find",
          method: "POST",
          body: {
            "LastEvaluatedKey": lastKey ? lastKey : undefined,
            "limit": 100,
            "startDate": filter.startDate,
            "endDate": filter.endDate,
            "status": filter.status,
          }
        });
        if (res.code === 200) {
          newTickets = [...newTickets, ...res.payload.tickets];
          setTickets(newTickets);
          setRows(newTickets);
          setIsLoading(false);
          lastKey = res.payload.LastEvaluatedKey;
        } else {
          setMessage('Error al obtener los tickets. Intenta nuevamente o comunícate con Soporte');
          setIsGlobalError(true);
          break;
        }
      } while (lastKey !== null);
    } catch (error) {
      console.error('Error in getTickets:', error);
      setMessage('Error al obtener los tickets. Intenta nuevamente o comunícate con Soporte');
      setIsGlobalError(true);
    }
    setIsLoading(false);
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = tickets.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(row[field].toString());
      });
    });
    setRows(filteredRows);
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  }

  const getColorCause = (cause) => {
    switch (cause) {
      case 'Error': return classes.red;
      case 'Sugerencia': return classes.blue;
      case 'Consulta': return classes.yellow;
      default: return undefined;
    }
  }

  const getColorState = (state) => {
    switch (state) {
      case 'Sin asignar': return classes.yellow;
      case 'Resuelto': return classes.green;
      case 'En progreso': return classes.blue;
      default: return undefined;
    }
  }

  const columns = [
    { id: 'id', label: 'Id ticket' },
    {
      id: 'cause',
      label: 'Causa',
      sortable: false,
      render: (row) => (
        <Chip size="small" label={row.cause} className={getColorCause(row.cause)} />
      ),
    },
    { id: 'subject', label: 'Asunto' },
    {
      id: 'creationDate',
      label: 'Fecha',
      type: 'date',
      render: (row) => moment(row.creationDate).utcOffset(row.creationDateZone).format("DD-MM-YYYY HH:mm"),
    },
    {
      id: 'status',
      label: 'Estado',
      sortable: false,
      render: (row) => (
        <Chip size="small" label={row.status} className={getColorState(row.status)} />
      ),
    },
    {
      id: 'username',
      label: 'Creador',
      format: (value) => value || '',
    },
  ];

  const renderError = () => (
    <>
      <Alert severity="error">
          {message}
      </Alert>
      <NeokodeButton
        type="button"
        variant="contained"
        className={classes.button}
        component={RouterLink}
        to="/"
      >
        Volver al inicio
      </NeokodeButton>
    </>
  );

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.gridMaxMd}>
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom>
            Tickets de Soporte
          </Typography>

          {
            isGlobalError ?
            renderError()
            :
            <>
              <FilterPanel onFilter={(data) => getTickets(data)} />
              <QuickSearchToolbar
                value={searchText}
                onChange={(e) => requestSearch(e.target.value)}
                clearSearch={() => requestSearch('')}
                onRefresh={() => getTickets()}
                addButtonUrl="/tickets/create"
                addButtonText="Agregar Ticket"
              />
              <DataTable
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                loading={isLoading}
                onRowClick={(row) => history.push(`/tickets/edit/${encodeURIComponent(row.id)}`)}
                initialSort={{ orderBy: 'creationDate', order: 'desc' }}
                emptyMessage="No hay tickets para mostrar."
                initialPageSize={10}
              />
            </>
          }
        </Paper>
      </Grid>
      <Snackbar open={openToast} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity="success">
          {messageToast}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default TicketsPage;
